import { useState, useEffect } from 'react';
import { Input, Button, Form, Select } from 'antd';
import { ArrowLeftOutlined, InfoCircleOutlined } from '@ant-design/icons';
import Balance from '../../../../../assets/Balance.jsx';
import CreditDebitCard from './CreditDebitCard.jsx';
import Stripe from './Stripe.jsx';
import ZaroPay from './ZaroPay.jsx';
import CashDeposit from '../../../wallet/deposit/components/CashDeposit.jsx';
import UpiDeposit from './UpiDeposit.jsx';
import ZinaCardPayment from './ZinaCardPayment.jsx';
import PayPal from './PayPal.jsx';
import { GetCurrentCurrencyRateApi } from '../../../../../api/request/wallet/cashdeposit.js';

const PaymentDetails = ({ paymentMethod, accounts, resetAll, onBack, walletBalance }) => {
    const [form] = Form.useForm();
    const [selectedAccount, setSelectedAccount] = useState('wallet');
    const [depositAmount, setDepositAmount] = useState(null);
    const [currencyRate, setCurrencyRate] = useState(null);
    const [isCashDeposit, setIsCashDeposit] = useState(false);
    const [validation, setValidation] = useState('');


    const getCurrentCurrencyRate = async () => {
        try {
            const { data } = await GetCurrentCurrencyRateApi();
            if (data?.success) setCurrencyRate(data?.data);
        } catch (error) {
        }
    }

    const handleAmountChange = (e) => {
        const value = parseFloat(e.target.value) || 0;
        setDepositAmount(value);
    };

    const renderStep = () => {
        switch (paymentMethod?.title) {

            case "Bank Transfer":
                return <CashDeposit amount={depositAmount} loginTerminalId={selectedAccount} resetAll={resetAll} validateAmount={validation} />;
            case "UPI":
                return <UpiDeposit amount={depositAmount} paymentMethod={paymentMethod} loginTerminalId={selectedAccount} resetAll={resetAll} validateAmount={validation} />;
            case "Easypaisa":
                return <ZinaCardPayment QrCode={paymentMethod?.QrCode} amount={depositAmount} paymentMethod={paymentMethod} loginTerminalId={selectedAccount} resetAll={resetAll} validateAmount={validation} />;
            case "Crypto":
                return <ZaroPay amount={depositAmount} paymentMethod={paymentMethod} accounts={accounts} loginTerminalId={selectedAccount} resetAll={resetAll} validateAmount={validation} />;
            case "Debit/Credit Card":
                return <Stripe amount={depositAmount} paymentMethod={paymentMethod} accounts={accounts} loginTerminalId={selectedAccount} resetAll={resetAll} validateAmount={validation} />;
            case "Network":
                return <CreditDebitCard amount={depositAmount} paymentMethod={paymentMethod} accounts={accounts} loginTerminalId={selectedAccount} resetAll={resetAll} validateAmount={validation} />;
            case "PayPal":
                return <PayPal amount={depositAmount} paymentMethod={paymentMethod} accounts={accounts} loginTerminalId={selectedAccount} resetAll={resetAll} validateAmount={validation} />;
            case "ZaroPay":
                return <ZaroPay amount={depositAmount} paymentMethod={paymentMethod} accounts={accounts} loginTerminalId={selectedAccount} resetAll={resetAll} validateAmount={validation} />;
            default:
                return null;
        }
    }

    const validateAmount = (_, value) => {
        const numericValue = parseFloat(value);
        const min = parseFloat(paymentMethod?.minAmount?.replace(/[^0-9.]/g, ""));
        const max = parseFloat(paymentMethod?.maxAmount?.replace(/[^0-9.]/g, ""));
        const usdValue = paymentMethod?.title === "Cash Deposit" ? depositAmount : numericValue;

        if (!value) {
            setValidation('Please enter deposit amount');
            return Promise.reject("Please enter deposit amount");
        }
        if (isNaN(numericValue)) {
            setValidation('Please enter valid number');
            return Promise.reject("Please enter valid number");
        }
        if (usdValue < min) {
            setValidation(`Minimum deposit is ${paymentMethod.minAmount}`);
            return Promise.reject(`Minimum deposit is ${paymentMethod.minAmount}`);
        }
        if (usdValue > max) {
            setValidation(`Maximum deposit is ${paymentMethod.maxAmount}`);
            return Promise.reject(`Maximum deposit is ${paymentMethod.maxAmount}`);
        }
        setValidation('');
        return Promise.resolve();
    }

    useEffect(() => {
        if (paymentMethod?.title === "Cash Deposit") {
            form.setFieldValue('depositAmount', depositAmount * currencyRate?.targetCurrencyDepositRate)
            setIsCashDeposit(true)
        } else {
            setIsCashDeposit(false)
        }
    }, [paymentMethod?.title])

    useEffect(() => {
        getCurrentCurrencyRate();
    }, []);

    return (
        <div className="w-full p-6 border-2 border-light rounded-md mt-3">
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                    <Button
                        type="text"
                        icon={<ArrowLeftOutlined />}
                        onClick={onBack}
                        className="text-gray-600"
                    />
                    <h1 className="text-xl font-semibold text-gray-800">Payment Details</h1>
                </div>
                <div className="text-primary px-4 py-1 rounded-lg font-medium flex items-center border border-primary">
                    <div>
                        <img src={paymentMethod?.paymentLogoUrl} alt={paymentMethod?.title} className="w-8 h-8 me-2" />
                    </div>
                    <div>
                        {paymentMethod?.title}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column - Payment Form */}
                <div className="lg:col-span-2">
                    <div className="space-y-6">
                        {/* Deposit Amount */}
                        <div className="bg-white rounded-lg border p-6">
                            <div className="grid md:grid-cols-2 gap-5">

                                <Form form={form} layout='vertical' className="mb-4 ">
                                    {
                                        isCashDeposit ?
                                            <div >
                                                <label className="text-lg font-medium text-gray-700 block mb-2">Deposit Amount In INR<span className='text-lg primary font-semibold'>
                                                    (${currencyRate?.baseCurrencyRate} = {currencyRate?.targetCurrencyDepositRate} {currencyRate?.targetCurrency})</span></label>
                                                <Form.Item name='depositAmount' rules={[{ validator: validateAmount }]}>
                                                    <Input
                                                        type='number'
                                                        prefix="₹"
                                                        addonAfter={`$ ${Number(depositAmount)?.toFixed(2)}`}
                                                        placeholder="0"
                                                        size='large'
                                                        onChange={(e) => {
                                                            const value = parseFloat(e.target.value) || 0;
                                                            setDepositAmount(value / currencyRate?.targetCurrencyDepositRate);
                                                        }}
                                                        value={depositAmount}
                                                    />
                                                </Form.Item>
                                            </div>
                                            :
                                            <div >
                                                <label className="text-lg font-medium text-gray-700 block mb-2">Deposit Amount In USD </label>
                                                <Form.Item name='depositAmount' rules={[{ validator: validateAmount }]}  >
                                                    <Input
                                                        type='number'
                                                        placeholder="0"
                                                        size='large'
                                                        prefix="$"
                                                        onChange={(e) => setDepositAmount(parseFloat(e.target.value) || 0)}
                                                        value={depositAmount}
                                                    />
                                                </Form.Item>
                                            </div>
                                    }
                                </Form>


                                {/* Where to Deposit Dropdown */}
                                <div className="mb-4">
                                    <label className="text-lg font-medium text-gray-700 block mb-2">Where to Deposit</label>
                                    <Select
                                        size="large"
                                        placeholder="Select account to deposit"
                                        className="w-full"
                                        onChange={(value) => setSelectedAccount(value)}
                                        value={selectedAccount}
                                    >
                                        <Select.Option value="wallet">
                                            <div className="flex items-center gap-3 py-1">
                                                <Balance color="black" className="size-6" />
                                                <div className="flex items-center">
                                                    <span className="text-base font-medium">Wallet</span>
                                                    <span className="text-sm text-gray-500 ms-2">${walletBalance || 0}</span>
                                                </div>
                                            </div>
                                        </Select.Option>
                                        {accounts?.map((item, index) => (
                                            <Select.Option key={index} value={item?.terminalID}>
                                                <div className="flex items-center gap-3 py-1">
                                                    <img
                                                        src="/assets/images/vertex_logo.png"
                                                        alt="Icon"
                                                        className="h-6 w-6 object-contain"
                                                    />
                                                    <div className="flex items-center">
                                                        <span className="text-base font-medium">{item?.terminalID}</span>
                                                        <span className="text-sm text-gray-500 ms-2">${item?.balance || 0}</span>
                                                    </div>
                                                </div>
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </div>
                            </div>
                            <div className="bg-white rounded-lg mt-5">
                                {renderStep()}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column - Transaction Summary */}
                <div className="lg:col-span-1 ">
                    <div className="bg-white rounded-lg border p-6 h-full justify-between">
                        <div>
                            <h2 className="text-xl font-semibold text-gray-800 mb-6">Transaction Summary</h2>
                            <div className="space-y-4">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Deposit Amount:</span>
                                    <span className="font-semibold">${depositAmount?.toFixed(2) || 0.00}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Processing Fee:</span>
                                    <span className="font-semibold">$0.00</span>
                                </div>
                                <hr className="border-gray-200" />
                                <div className="flex justify-between text-lg">
                                    <span className="font-semibold">Total Amount:</span>
                                    <span className="font-bold text-green-600">${depositAmount?.toFixed(2) || 0.00}</span>
                                </div>
                            </div>
                        </div>
                        {/* Important Information */}
                        <div className="bg-blue-50 rounded-lg p-4 mt-4">
                            <div className="flex items-start gap-2 mb-3">
                                <InfoCircleOutlined className="text-blue-600 mt-1" />
                                <h3 className="font-semibold text-blue-800">Important Information</h3>
                            </div>
                            <ul className="text-sm text-blue-700 space-y-2">
                                <li>• Funds will be available instantly after successful payment</li>
                                <li>• All transactions are encrypted with SSL technology</li>
                                <li>• Transaction fees may apply based on payment method</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>


        </div>
    );
};

export default PaymentDetails;